import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import { REASSIGN_REASONS, departmentLabelKey, reassignReasonLabelKey } from '@nbh/shared';
import type { AssigneeWorkload, Department, MessageableUser } from '@nbh/shared';
import { api } from '../../lib/api';

interface ReassignSheetProps {
  /** The department the job sits in now, so a cross-department move can be announced. */
  currentDepartment: Department;
  /** Who holds it now. Null on an unassigned job — the same sheet dispatches one. */
  currentAssignee: { id: string | null; name: string | null };
  onCancel: () => void;
  /** Moves the job. The reason is already trimmed, and may be undefined. */
  onConfirm: (assigneeId: string, reason: string | undefined) => Promise<void>;
}

/** One candidate, with the two things a manager decides on: their team and their load. */
interface Candidate extends MessageableUser {
  openJobs: number;
}

/**
 * Handing a job to somebody else (decision D38).
 *
 * The list comes from `GET /api/messages/directory`, which every signed-in role may reach
 * — unlike everything under `/admin`, which is `@Roles(ADMIN)` and so unreachable by
 * FRONT_DESK and SUPERVISOR, the two roles this feature exists for. It already filters
 * deactivated accounts and already excludes EXTERNAL_VENDOR at source, which is what R10
 * was reaching for: not "a contractor may never hold a job" — the server permits that, and
 * §5.3 depends on it — but "do not let a manager pick one by accident off a staff list".
 *
 * Every name carries its current live-job count. Handing the next job to whoever is
 * already carrying the most is the common way a shift reshuffle makes things worse, and
 * the number is the cheapest possible guard against it.
 *
 * Grouped by department and searchable, because a property has more staff than fits on a
 * phone screen and a manager usually knows the team before the person.
 *
 * The reason is optional and stays optional. Reject demands one (D7) because a technician
 * cannot act on "rejected" alone; the person losing a job has the same question, but a
 * duty manager moving six jobs at shift change has six reasons to type, and a mandatory
 * field there buys silence rather than information. So: four chips, one tap each, plus
 * free text. Submitting with nothing costs exactly the same number of taps.
 */
export function ReassignSheet({
  currentDepartment,
  currentAssignee,
  onCancel,
  onConfirm,
}: ReassignSheetProps) {
  const { t } = useTranslation();
  const { candidates, loadFailed } = useDirectory(currentAssignee.id);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<Candidate | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = useMemo(() => search(candidates, query), [candidates, query]);

  // Stated, not offered as a second toggle: the queue follows the person, because the
  // queue is how staff find work and a job left in the old one is invisible to the team
  // now expected to do it.
  const movingQueue = chosen !== null && chosen.department !== currentDepartment;

  async function confirm(): Promise<void> {
    if (!chosen) return;

    setBusy(true);
    setError(null);
    try {
      // TODO(offline-transitions): fetch-only by deliberate exception to Rule 4 — see docs/DEBT.md.
      await onConfirm(chosen.id, reason.trim() || undefined);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('reassign.failed'));
      setBusy(false);
    }
  }

  // Portalled for the same reason RejectSheet and ProofSheet are: the action toolbar is
  // `position: sticky`, which creates a stacking context unconditionally, so a sheet
  // rendered inside it sits beneath the fixed tab bar however high its z-index goes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end bg-black/70">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('reassign.heading')}
        className="flex max-h-[88vh] w-full flex-col rounded-t-2xl border-t border-slate-800 bg-slate-950 pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)]"
      >
        <header className="flex items-center justify-between px-4 pb-2 pt-4">
          <p className="text-base font-semibold text-slate-100">{t('reassign.heading')}</p>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('reassign.cancel')}
            className="flex size-12 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </header>

        <p className="px-4 pb-3 text-sm text-slate-400">
          {currentAssignee.name
            ? t('reassign.explainHeld', { name: currentAssignee.name })
            : t('reassign.explainUnassigned')}
        </p>

        <CandidateList
          candidates={matches}
          query={query}
          onQuery={setQuery}
          chosenId={chosen?.id ?? null}
          onChoose={setChosen}
          loadFailed={loadFailed}
        />

        <div className="border-t border-slate-800 px-4 pb-6 pt-3">
          {movingQueue && chosen && (
            <p className="mb-2 rounded-lg bg-sky-950/60 px-3 py-2 text-xs text-sky-200">
              {t('reassign.departmentMove', {
                department: t(departmentLabelKey[chosen.department]),
              })}
            </p>
          )}

          <ReasonPicker reason={reason} onReason={setReason} />

          {error && (
            <p role="alert" className="mb-2 text-sm text-red-400">
              {error}
            </p>
          )}

          {/* Disabled for want of a person, never for want of a reason. */}
          <button
            type="button"
            disabled={!chosen || busy}
            onClick={() => void confirm()}
            className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-brookfields-green px-4 text-base font-semibold text-white ring-1 ring-brookfields-gold/70 disabled:bg-slate-800 disabled:text-slate-500 disabled:ring-0"
          >
            {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
            {chosen ? t('reassign.confirmTo', { name: chosen.name }) : t('reassign.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface CandidateListProps {
  candidates: Candidate[];
  query: string;
  onQuery: (query: string) => void;
  chosenId: string | null;
  onChoose: (person: Candidate) => void;
  loadFailed: boolean;
}

/** The scrolling half: search, then the staff grouped by team. */
function CandidateList({
  candidates,
  query,
  onQuery,
  chosenId,
  onChoose,
  loadFailed,
}: CandidateListProps) {
  const { t } = useTranslation();
  const grouped = useMemo(() => groupByDepartment(candidates), [candidates]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4">
      <label className="relative mb-3 block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-slate-500"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          aria-label={t('reassign.searchLabel')}
          placeholder={t('reassign.searchPlaceholder')}
          className="min-h-[48px] w-full rounded-xl border border-slate-700 bg-slate-900 pl-11 pr-3 text-base text-slate-100 placeholder:text-slate-500"
        />
      </label>

      {loadFailed && (
        <p role="alert" className="py-4 text-sm text-red-400">
          {t('reassign.directoryFailed')}
        </p>
      )}

      {!loadFailed && candidates.length === 0 && (
        <p className="py-4 text-sm text-slate-500">
          {query.trim() ? t('reassign.noMatches') : t('reassign.loading')}
        </p>
      )}

      {grouped.map(([department, people]) => (
        <section key={department} className="mb-4">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t(departmentLabelKey[department])}
          </h3>
          <ul className="space-y-1">
            {people.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  aria-pressed={chosenId === person.id}
                  onClick={() => onChoose(person)}
                  className={`flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl px-3 text-left text-base ${
                    chosenId === person.id
                      ? 'bg-brookfields-green text-white ring-1 ring-brookfields-gold/70'
                      : 'bg-slate-900 text-slate-100'
                  }`}
                >
                  <span className="truncate">{person.name}</span>
                  <span className="shrink-0 text-xs text-slate-400">
                    {t('reassign.openJobs', { count: person.openJobs })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Four chips and a text field, over one value.
 *
 * One value on purpose: a chip and a typed sentence are the same field answered two ways,
 * and keeping them apart would mean deciding which of two reasons to send.
 */
function ReasonPicker({
  reason,
  onReason,
}: {
  reason: string;
  onReason: (reason: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="mb-2 flex flex-wrap gap-2" role="group" aria-label={t('reassign.why')}>
        {REASSIGN_REASONS.map((chip) => {
          // What a chip stores is the copy on its face. `reason` is free text rendered
          // back verbatim in the trail, so a Spanish duty manager's row should read in
          // Spanish rather than in a code nothing on the render side translates.
          const label = t(reassignReasonLabelKey(chip));

          return (
            <button
              key={chip}
              type="button"
              aria-pressed={reason === label}
              onClick={() => onReason(reason === label ? '' : label)}
              className={`min-h-[44px] rounded-full px-4 text-sm font-medium ${
                reason === label ? 'bg-slate-200 text-slate-900' : 'bg-slate-800 text-slate-300'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <input
        type="text"
        value={reason}
        onChange={(event) => onReason(event.target.value)}
        aria-label={t('reassign.reasonLabel')}
        placeholder={t('reassign.reasonPlaceholder')}
        className="mb-2 min-h-[48px] w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-base text-slate-100 placeholder:text-slate-500"
      />
    </>
  );
}

/**
 * Who the job could go to, and how much each of them is already carrying.
 *
 * The directory failing is reported, not swallowed — it is the whole content of this
 * sheet, unlike on the report form where the picker is an enhancement over the department
 * pool and an unreachable directory must not stop a fault being filed. The counts are the
 * enhancement here, so a failure there leaves every badge at zero rather than emptying
 * the list.
 *
 * The person already holding the job is dropped: the server refuses a no-op reassignment,
 * and offering a choice that can only produce an error is worse than not offering it.
 */
function useDirectory(currentAssigneeId: string | null) {
  const [staff, setStaff] = useState<MessageableUser[]>([]);
  const [workload, setWorkload] = useState<AssigneeWorkload[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let live = true;

    void Promise.all([
      api.messageDirectory(),
      api.ticketWorkload().catch(() => [] as AssigneeWorkload[]),
    ])
      .then(([people, counts]) => {
        if (!live) return;
        setStaff(people);
        setWorkload(counts);
      })
      .catch(() => live && setLoadFailed(true));

    return () => {
      live = false;
    };
  }, []);

  const candidates = useMemo(() => {
    const counts = new Map(workload.map((entry) => [entry.userId, entry.openJobs]));

    return staff
      .filter((person) => person.id !== currentAssigneeId)
      .map((person) => ({ ...person, openJobs: counts.get(person.id) ?? 0 }));
  }, [staff, workload, currentAssigneeId]);

  return { candidates, loadFailed };
}

/** Substring match on the name, case-insensitively. Empty query matches everyone. */
function search(candidates: Candidate[], query: string): Candidate[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return candidates;

  return candidates.filter((person) => person.name.toLowerCase().includes(needle));
}

/**
 * The candidates by team.
 *
 * A Map rather than an object so insertion order is the directory's own — already sorted
 * by name server-side — which makes the group order stable between renders instead of
 * dependent on how the runtime enumerates keys.
 */
function groupByDepartment(candidates: Candidate[]): Array<[Department, Candidate[]]> {
  const groups = new Map<Department, Candidate[]>();

  for (const person of candidates) {
    const team = groups.get(person.department);
    if (team) team.push(person);
    else groups.set(person.department, [person]);
  }

  return [...groups.entries()];
}
